import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  Download,
  GraduationCap,
  History,
  Landmark,
  LockKeyhole,
  Monitor,
  QrCode,
  ShieldCheck,
  Smartphone,
  Store,
  UserRound,
  UsersRound,
  WalletCards,
} from 'lucide-react';
import PageMeta from '../components/PageMeta';
import {
  MASOMO_SMART_DOWNLOAD_PATH,
  WONYAPAY_SMART_DOWNLOAD_PATH,
} from '../constants/links';
import { useLanguage } from '../i18n/LanguageProvider';
import appImage from '../assets/3.webp';

const applications = [
  {
    name: 'WonyaPay',
    downloadPath: WONYAPAY_SMART_DOWNLOAD_PATH,
    icon: WalletCards,
    category: 'Paiements et gestion de projets',
    description: 'Payez, encaissez et retrouvez vos projets, factures et opérations depuis votre téléphone ou votre ordinateur.',
  },
  {
    name: 'Masomo',
    downloadPath: MASOMO_SMART_DOWNLOAD_PATH,
    icon: GraduationCap,
    category: 'Gestion scolaire',
    description: 'Retrouvez les élèves, les classes, les frais scolaires, les présences et les bulletins selon les droits accordés par votre école.',
  },
];

const DownloadApp = () => {
  const { t } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    { icon: WalletCards, title: 'Paiements Mobile Money', text: 'Utilisez les services Mobile Money disponibles dans un parcours unifié.' },
    { icon: Smartphone, title: 'Encaissement mobile', text: 'Recevez et suivez les paiements directement depuis votre smartphone.' },
    { icon: QrCode, title: 'QR Code', text: 'Payez ou identifiez une opération rapidement grâce au scan.' },
    { icon: History, title: 'Historique et suivi', text: 'Consultez les transactions, leur statut et les reçus de paiement.' },
  ];

  const audiences = [
    { icon: UserRound, label: 'Particuliers' },
    { icon: Store, label: 'Marchands' },
    { icon: GraduationCap, label: 'Écoles et universités' },
    { icon: Building2, label: 'Entreprises' },
    { icon: Landmark, label: 'Institutions' },
    { icon: ShieldCheck, label: 'Services publics' },
    { icon: UsersRound, label: 'Organisations et projets' },
  ];

  return (
    <div className="ws-page ws-app" data-no-i18n="true">
      <PageMeta
        title="Télécharger WonyaPay et Masomo | WonyaSoft"
        description="Téléchargez WonyaPay et Masomo sur Android, iPhone, iPad, macOS et Windows depuis leurs stores officiels."
      />

      <section className="ws-app-hero">
        <div className="ws-shell ws-app-hero__grid">
          <div className="ws-app-hero__copy">
            <span className="ws-eyebrow"><Smartphone size={18} aria-hidden="true" /> {t('Applications WonyaSoft')}</span>
            <h1>{t('WonyaPay et Masomo, sur tous vos appareils')}</h1>
            <p>{t('Vos paiements et votre école vous accompagnent. Retrouvez nos applications sur mobile et ordinateur, depuis leurs stores officiels.')}</p>
            <div className="ws-button-row">
              <Link className="ws-button ws-button--primary" to={WONYAPAY_SMART_DOWNLOAD_PATH}>
                <Download size={18} aria-hidden="true" />
                {t('Télécharger')} WonyaPay
              </Link>
              <Link className="ws-button ws-button--secondary" to={MASOMO_SMART_DOWNLOAD_PATH}>
                <Download size={18} aria-hidden="true" />
                {t('Télécharger')} Masomo
              </Link>
            </div>
            <div className="ws-app-hero__proofs">
              <span><CheckCircle2 size={18} aria-hidden="true" /> {t('Paiements Mobile Money')}</span>
              <span><GraduationCap size={18} aria-hidden="true" /> {t('Gestion scolaire')}</span>
              <span><Monitor size={18} aria-hidden="true" /> {t('Mobile et ordinateur')}</span>
            </div>
          </div>
          <div className="ws-app-hero__image">
            <div className="ws-app-hero__image-frame">
              <img src={appImage} alt={t('Utilisatrice de l’application mobile WonyaPay')} fetchPriority="high" />
            </div>
            <div className="ws-app-hero__floating-card">
              <LockKeyhole size={20} aria-hidden="true" />
              <span>{t('Des opérations simples et sécurisées')}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="ws-section ws-section--soft ws-app-catalog" id="download" aria-labelledby="ws-app-download-title">
        <div className="ws-shell">
          <div className="ws-section-heading ws-section-heading--center">
            <span className="ws-kicker">{t('Téléchargements')}</span>
            <h2 id="ws-app-download-title">{t('Choisissez votre application')}</h2>
            <p>{t('Le bouton ouvre automatiquement le store adapté à votre appareil.')}</p>
          </div>
          <div className="ws-app-catalog__grid">
            {applications.map(({ name, downloadPath, icon: AppIcon, category, description }) => (
              <article className="ws-app-card" key={name} aria-labelledby={`ws-app-${name}-title`}>
                <div className="ws-app-card__heading">
                  <span className="ws-app-card__icon"><AppIcon size={30} aria-hidden="true" /></span>
                  <div>
                    <h3 id={`ws-app-${name}-title`}>{name}</h3>
                    <span className="ws-app-card__category">{t(category)}</span>
                  </div>
                </div>
                <p className="ws-app-card__description">{t(description)}</p>
                <Link className="ws-button ws-button--primary ws-app-card__smart-download" to={downloadPath} aria-label={`${t('Télécharger')} ${name}`}>
                  <Download size={18} aria-hidden="true" />
                  {t('Télécharger')}
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="ws-section" aria-labelledby="ws-app-features-title">
        <div className="ws-shell">
          <div className="ws-section-heading ws-section-heading--center">
            <span className="ws-kicker">WonyaPay</span>
            <h2 id="ws-app-features-title">{t('Une expérience mobile pensée pour le quotidien')}</h2>
            <p>{t('WonyaPay réunit les opérations essentielles dans une interface simple, adaptée aux particuliers comme aux organisations.')}</p>
          </div>
          <div className="ws-feature-grid">
            {features.map(({ icon: Icon, title, text }) => (
              <article className="ws-feature-card" key={title}>
                <Icon size={25} aria-hidden="true" />
                <h3>{t(title)}</h3>
                <p>{t(text)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="ws-section ws-section--soft" aria-labelledby="ws-app-audience-title">
        <div className="ws-shell">
          <div className="ws-section-heading">
            <span className="ws-kicker">WonyaPay</span>
            <h2 id="ws-app-audience-title">{t('Pour qui est conçue WonyaPay ?')}</h2>
          </div>
          <div className="ws-app-audiences">
            {audiences.map(({ icon: Icon, label }) => (
              <div key={label}><Icon size={21} aria-hidden="true" /><span>{t(label)}</span></div>
            ))}
          </div>
        </div>
      </section>

      <section className="ws-section">
        <div className="ws-shell ws-app-security">
          <div className="ws-app-security__icon"><ShieldCheck size={42} aria-hidden="true" /></div>
          <div>
            <span className="ws-kicker">{t('Sécurité')}</span>
            <h2>{t('Des opérations simples et sécurisées')}</h2>
            <p>{t('La sécurité, la traçabilité et la clarté des statuts sont intégrées au parcours afin que chaque utilisateur garde une lecture précise de ses opérations.')}</p>
          </div>
        </div>
      </section>

      <section className="ws-section">
        <div className="ws-shell ws-app-download">
          <div>
            <span className="ws-kicker">WonyaPay &amp; Masomo</span>
            <h2>{t('Vos applications à portée de main')}</h2>
            <p>{t('Retrouvez WonyaPay et Masomo sur votre téléphone, votre tablette ou votre ordinateur.')}</p>
          </div>
          <div className="ws-button-row">
            <a className="ws-button ws-button--primary" href="#download">{t('Voir les téléchargements')} <ArrowRight size={18} aria-hidden="true" /></a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default DownloadApp;
